import { useParams } from 'react-router';
import { businessDate, money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { type Allocation } from './kit/hooks';
import { ALLOCATION_METHODS, docType, links, METHOD_KINDS, PURPOSES } from './kit/labels';
import { PrintDocument } from './kit/PrintDocument';
import { BackLink, DetailSkeleton, ErrorState } from './kit/ui';
import { usePayment } from './PaymentDetailPage';

/** A4 payment receipt / remittance advice with what the payment settled. */
export function PaymentPrintPage() {
  const { id = '' } = useParams();
  const { format } = useShop();
  const payment = usePayment(id);

  if (payment.isLoading) return <DetailSkeleton />;
  if (payment.isError || !payment.data)
    return (
      <>
        <BackLink to={links.payment(id)} label="Payment" />
        <ErrorState error={payment.error} onRetry={() => void payment.refetch()} />
      </>
    );

  const p = payment.data;
  const meta = PURPOSES[p.purpose];
  const title =
    p.purpose === 'RECEIPT'
      ? 'Payment receipt'
      : p.purpose === 'SUPPLIER_PAYMENT'
        ? 'Remittance advice'
        : 'Refund';
  const rows = p.allocations.filter((a) => !a.reversed);

  return (
    <PrintDocument<Allocation>
      title={title}
      number={p.number}
      status={p.status === 'VOIDED' ? 'Voided' : undefined}
      backTo={links.payment(p.id)}
      party={{
        label: meta.party === 'customer' ? 'Received from' : 'Paid to',
        name: p.contact.name,
        lines: [p.contact.phone],
      }}
      meta={[
        { label: 'Date', value: businessDate(p.paymentDate, format) },
        { label: 'Method', value: `${p.method.name} (${METHOD_KINDS[p.method.kind]})` },
        ...(p.reference ? [{ label: 'Reference', value: p.reference }] : []),
      ]}
      columns={[
        {
          key: 'doc',
          header: 'Document',
          render: (a) => {
            const other = a.credit.id === p.id ? a.target : a.credit;
            return other.number ?? docType(other.type);
          },
        },
        {
          key: 'type',
          header: 'Type',
          render: (a) => docType(a.credit.id === p.id ? a.target.type : a.credit.type),
        },
        { key: 'how', header: 'Applied', render: (a) => ALLOCATION_METHODS[a.method] ?? a.method },
        {
          key: 'amount',
          header: 'Amount',
          align: 'right',
          width: '28%',
          render: (a) => money(a.amount, format),
        },
      ]}
      rows={rows}
      rowKey={(a) => a.id}
      totals={[
        { label: 'Applied to documents', value: money(p.allocated, format) },
        {
          label:
            p.purpose === 'RECEIPT' || p.purpose === 'SUPPLIER_PAYMENT'
              ? 'Credit on account'
              : 'Unmatched',
          value: money(p.unallocated, format),
        },
        { label: 'Amount', value: money(p.amount, format), strong: true },
      ]}
      notes={[p.notes, p.voidReason ? `Voided: ${p.voidReason}` : null]}
    />
  );
}
